import { useMemo, useState } from "react";
import type { CropChip } from "../../lib/api";
import { DEFAULT_TOP_CROPS, emojiForCrop, type TopCrop } from "../../lib/cropRegions";

/* ────────────────────────────────────────────────────────────────────────────
   CropChipPicker — pick crops as chips instead of a single-select + free text.
   Tap an icon tile from the "popular crops" grid (the top ~20 we commonly grow),
   or search/add anything beyond that as a labeled chip (metadata, not free text).
   Custom chips can optionally be proposed to the backend (authed) so a superadmin
   can promote them into the global catalog. Value = CropChip[] ({name, growable_id?}).
   Theme-aware (CSS vars) so it reads in light + dark mode.
──────────────────────────────────────────────────────────────────────────── */

export function CropChipPicker({
  value,
  onChange,
  suggestions,
  onProposeCustom,
  topCrops = DEFAULT_TOP_CROPS,
  placeholder = "Search for another crop, or type your own…",
}: {
  value: CropChip[];
  onChange: (v: CropChip[]) => void;
  suggestions: { id?: string; common_name: string }[];
  onProposeCustom?: (name: string) => Promise<string | undefined>;
  topCrops?: TopCrop[];
  placeholder?: string;
}) {
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const chosen = useMemo(() => new Set(value.map((c) => c.name.toLowerCase())), [value]);

  // Emoji lookup: top-crop names first, then aliases, else a generic sprout.
  const emojiByName = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of topCrops) m.set(t.name.toLowerCase(), t.emoji);
    return m;
  }, [topCrops]);
  const emojiFor = (name: string): string =>
    emojiByName.get(name.toLowerCase()) ?? emojiForCrop(name);

  // If a picked name exists in the catalog/suggestions, carry its id (so
  // onboarding can link the materialized variety back to the catalog row).
  const suggestionIdFor = (name: string): string | undefined =>
    suggestions.find((s) => s.common_name.toLowerCase() === name.toLowerCase())?.id;

  const q = input.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [];
    return suggestions
      .filter((s) => s.common_name.toLowerCase().includes(q) && !chosen.has(s.common_name.toLowerCase()))
      .slice(0, 8);
  }, [q, suggestions, chosen]);
  const exact = suggestions.some((s) => s.common_name.toLowerCase() === q);

  function add(chip: CropChip) {
    const name = chip.name.trim();
    if (!name || chosen.has(name.toLowerCase())) {
      setInput("");
      return;
    }
    onChange([...value, { name, growable_id: chip.growable_id }]);
    setInput("");
    setOpen(false);
  }
  function remove(name: string) {
    onChange(value.filter((v) => v.name.toLowerCase() !== name.toLowerCase()));
  }
  function toggleTop(crop: TopCrop) {
    if (chosen.has(crop.name.toLowerCase())) remove(crop.name);
    else onChange([...value, { name: crop.name, growable_id: suggestionIdFor(crop.name) }]);
  }
  async function addCustom() {
    const name = input.trim();
    if (!name || chosen.has(name.toLowerCase())) {
      setInput("");
      return;
    }
    let gid: string | undefined = suggestionIdFor(name);
    if (!gid && onProposeCustom) {
      setBusy(true);
      try {
        gid = await onProposeCustom(name);
      } catch {
        /* still add the chip locally — materialization retries on the server */
      } finally {
        setBusy(false);
      }
    }
    add({ name, growable_id: gid });
  }
  function onKey(e: React.KeyboardEvent) {
    if (e.key === "Enter") {
      e.preventDefault();
      if (matches.length && !exact) add({ name: matches[0].common_name, growable_id: matches[0].id });
      else void addCustom();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const chipStyle: React.CSSProperties = {
    display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 8px 5px 10px",
    borderRadius: 20, fontSize: 13, fontWeight: 500,
    background: "color-mix(in srgb, var(--brand-mango) 14%, transparent)",
    border: "1px solid color-mix(in srgb, var(--brand-mango) 45%, transparent)",
    color: "var(--color-text-primary)",
  };

  return (
    <div>
      {/* Selected crops — the running list (also reflected as highlighted tiles below). */}
      {value.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          {value.map((c) => (
            <span key={c.name} style={chipStyle}>
              <span aria-hidden="true">{emojiFor(c.name)}</span>
              {c.name}
              {!c.growable_id && (
                <span title="Custom — we'll add it for you" style={{ fontSize: 10, opacity: 0.7 }}>✎</span>
              )}
              <button type="button" onClick={() => remove(c.name)}
                aria-label={`Remove ${c.name}`}
                style={{
                  border: "none", background: "transparent", cursor: "pointer",
                  color: "var(--color-text-tertiary)", fontSize: 16, lineHeight: 1, padding: 0, marginLeft: 2,
                }}>×</button>
            </span>
          ))}
        </div>
      )}

      {/* Popular crops — tap an icon tile to add/remove. */}
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 8 }}>
        Popular crops
      </div>
      <div role="group" aria-label="Popular crops"
        style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: 8, marginBottom: 14 }}>
        {topCrops.map((crop) => {
          const selected = chosen.has(crop.name.toLowerCase());
          return (
            <button key={crop.name} type="button"
              onClick={() => toggleTop(crop)}
              aria-pressed={selected}
              aria-label={`${selected ? "Remove" : "Add"} ${crop.name}`}
              style={{
                position: "relative", display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                padding: "10px 6px", borderRadius: 12, cursor: "pointer", minHeight: 68,
                background: selected
                  ? "color-mix(in srgb, var(--brand-mango) 18%, var(--color-bg-raised))"
                  : "var(--color-bg-raised)",
                border: selected
                  ? "1.5px solid var(--brand-mango)"
                  : "1px solid var(--color-border-medium)",
                color: "var(--color-text-primary)", transition: "background 0.12s, border-color 0.12s",
              }}>
              {selected && (
                <span aria-hidden="true" style={{
                  position: "absolute", top: 3, right: 5, fontSize: 11, color: "var(--brand-mango)", fontWeight: 700,
                }}>✓</span>
              )}
              <span aria-hidden="true" style={{ fontSize: 24, lineHeight: 1 }}>{crop.emoji}</span>
              <span style={{ fontSize: 11, textAlign: "center", lineHeight: 1.15 }}>{crop.name}</span>
            </button>
          );
        })}
      </div>

      {/* Search / add anything else. */}
      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 6 }}>
        Something else?
      </div>
      <div style={{ position: "relative" }}>
        <input
          value={input}
          onChange={(e) => { setInput(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKey}
          placeholder={placeholder}
          style={{
            width: "100%", padding: "10px 14px", fontSize: 15, borderRadius: 10,
            background: "var(--color-bg-raised)", border: "1px solid var(--color-border-medium)",
            color: "var(--color-text-primary)", outline: "none", boxSizing: "border-box",
          }}
        />
        {open && (q.length > 0) && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
            background: "var(--color-bg-card)", border: "1px solid var(--color-border)",
            borderRadius: 10, overflow: "hidden", maxHeight: 260, overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          }}>
            {matches.map((m) => (
              <button key={m.common_name} type="button"
                onMouseDown={(e) => { e.preventDefault(); add({ name: m.common_name, growable_id: m.id }); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", padding: "9px 14px",
                  border: "none", background: "transparent", cursor: "pointer", fontSize: 14,
                  color: "var(--color-text-primary)",
                }}>
                <span aria-hidden="true">{emojiFor(m.common_name)}</span>
                {m.common_name}
              </button>
            ))}
            {!exact && (
              <button type="button" disabled={busy}
                onMouseDown={(e) => { e.preventDefault(); void addCustom(); }}
                style={{
                  display: "block", width: "100%", textAlign: "left", padding: "9px 14px",
                  border: "none", borderTop: matches.length ? "1px solid var(--color-border)" : "none",
                  background: "transparent", cursor: busy ? "wait" : "pointer", fontSize: 14, fontWeight: 500,
                  color: "var(--brand-mango)",
                }}>
                {busy ? "Adding…" : `＋ Add "${input.trim()}"`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
